import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClusterListItem } from '@b4b/shared';
import { useSetRead, useSetSaved, useAddMute } from '../api/queries.js';
import { useRelativeTime } from '../lib/time.js';
import { displaySummary, displayTitle } from '../lib/cardText.js';
import Placeholder from './Placeholder.js';
import TranslatedBadge from './TranslatedBadge.js';

// Reels-style full-screen story card: tap opens the detail, the action rail
// sits on the right edge, one card per viewport (scroll-snap in Feed).
export default function ReelCard({ item }: { item: ClusterListItem }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const relTime = useRelativeTime();
  const setRead = useSetRead();
  const setSaved = useSetSaved();
  const addMute = useAddMute();
  const [imgFailed, setImgFailed] = useState(false);
  const [muteOpen, setMuteOpen] = useState(false);

  const rep = item.representative;
  const extraSources = item.sources.length - 1;

  const openOriginal = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(rep.url, '_blank', 'noopener');
    if (!item.read) setRead.mutate({ id: item.id, read: true });
  };

  const muteCandidates = [
    ...new Set(
      rep.title
        .split(/\s+/)
        .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
        .filter((w) => w.length >= 4),
    ),
  ].slice(0, 6);

  const railButton =
    'flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-lg text-white backdrop-blur-sm transition hover:bg-black/60';

  return (
    <section
      data-cluster-id={item.id}
      onClick={() => navigate(`/c/${item.id}`)}
      className="relative h-full w-full cursor-pointer snap-start snap-always overflow-hidden bg-neutral-900"
    >
      {/* background image / placeholder */}
      {rep.imageUrl && !imgFailed ? (
        <img
          src={rep.imageUrl}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setImgFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <Placeholder title={rep.title} faviconUrl={rep.faviconUrl} className="absolute inset-0" />
      )}
      {/* readability gradient — only over the bottom text block, the top of the photo stays untinted */}
      <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.95)_0%,rgba(0,0,0,0.8)_25%,rgba(0,0,0,0.45)_45%,rgba(0,0,0,0.15)_58%,rgba(0,0,0,0)_68%)]" />
      {rep.imageGenerated && rep.imageUrl && !imgFailed && (
        // honesty label: this picture is an AI illustration, not a news photo
        <span className="absolute left-3 top-16 z-10 rounded-full bg-black/45 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white/80 backdrop-blur-sm">
          ✦ {t('feed.aiImage')}
        </span>
      )}

      {/* action rail — bottom-aligned with the story text, above the gesture bar */}
      <div
        className="absolute right-3 z-10 flex flex-col gap-3"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setSaved.mutate({ id: item.id, saved: !item.saved })}
          aria-label={item.saved ? t('article.unsave') : t('article.save')}
          className={`${railButton} ${item.saved ? '!bg-amber-500/90' : ''}`}
        >
          {item.saved ? '★' : '☆'}
        </button>
        <button
          onClick={() => setRead.mutate({ id: item.id, read: !item.read })}
          aria-label={item.read ? t('article.markUnread') : t('article.markRead')}
          className={`${railButton} ${item.read ? '!bg-accent/90' : ''}`}
        >
          ✓
        </button>
        <button onClick={openOriginal} aria-label={t('feed.openOriginal')} className={railButton}>
          ↗
        </button>
        <button
          onClick={() => setMuteOpen((v) => !v)}
          aria-label={t('feed.mute')}
          className={railButton}
        >
          ⋯
        </button>
      </div>

      {muteOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-24 right-16 z-20 w-56 rounded-xl border border-neutral-700 bg-neutral-900/95 p-2 shadow-lg backdrop-blur"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
        >
          <p className="px-2 py-1 text-xs font-semibold text-neutral-400">{t('feed.mute')}</p>
          {muteCandidates.map((word) => (
            <button
              key={word}
              onClick={() => {
                addMute.mutate({ keyword: word });
                setMuteOpen(false);
              }}
              className="block w-full rounded-lg px-2 py-1.5 text-left text-sm text-white hover:bg-neutral-800"
            >
              {t('feed.muteWord', { word })}
            </button>
          ))}
        </div>
      )}

      {/* story content — anchored to the bottom edge (Reels-style), clear of the gesture bar */}
      <div
        className="absolute inset-x-0 bottom-0 z-0 p-5 pr-20 text-white"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
      >
        <p className="mb-2 flex items-center gap-2 text-xs text-white/70">
          {rep.faviconUrl && (
            <img
              src={rep.faviconUrl}
              alt=""
              referrerPolicy="no-referrer"
              className="h-4 w-4 rounded"
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          )}
          <span>
            {rep.feedTitle ?? ''} · {relTime(rep.publishedAt)}
            {rep.readingTimeMin ? ` · ${t('feed.readingTime', { min: rep.readingTimeMin })}` : ''}
            {item.read && ' · ✓'}
          </span>
          {rep.translation && <TranslatedBadge translation={rep.translation} />}
        </p>
        <h2 className="text-2xl font-bold leading-tight [text-shadow:0_1px_3px_rgba(0,0,0,.5)]">
          {displayTitle(rep)}
        </h2>
        {displaySummary(rep) && (
          <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-white/85">{displaySummary(rep)}</p>
        )}
        {rep.tags.length > 0 && (
          // AI tags as context; tap = show all stories with this tag
          <p className="mt-2 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
            {rep.tags.slice(0, 4).map((tag) => (
              <button
                key={tag}
                onClick={() => navigate(`/?tag=${encodeURIComponent(tag)}`)}
                className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] text-white/80 backdrop-blur-sm hover:bg-white/25"
              >
                #{tag}
              </button>
            ))}
          </p>
        )}
        {extraSources > 0 && (
          <p className="mt-2 text-xs text-white/70">
            <span className="mr-1 rounded-full bg-white/20 px-2 py-0.5 font-medium">
              {t('feed.sourcesBadge', { count: extraSources })}
            </span>
            {t('feed.alsoOn')}{' '}
            {item.sources
              .filter((s) => s.feedId !== rep.feedId)
              .slice(0, 3)
              .map((s) => s.feedTitle)
              .filter(Boolean)
              .join(', ')}
          </p>
        )}
      </div>
    </section>
  );
}
